import { forwardRef, useMemo } from 'react'
import type { InstructionHandbookSectionRecord } from '@artem-lex/compliance-documents'
import type { Editor } from '@tiptap/core'
import { useTranslation } from 'react-i18next'
import { ComplianceDocumentView } from '../../../shared/compliance-document/ComplianceDocumentView'
import type { ComplianceDocumentViewHandle } from '../../../shared/compliance-document/complianceDocumentViewHandle'
import { getSectionHeadingLabel } from '../../../shared/compliance-document/complianceDocumentEditorUtils'
import { useComplianceDocumentManifestContext } from '../../../shared/compliance-document/ComplianceDocumentManifestContext'
import { ComplianceDocumentStreamingPanel } from '../../../shared/compliance-document/ComplianceDocumentStreamingPanel'
import { shouldShowInstructionHandbookSectionEditor } from './instructionHandbookSectionUi'

type InstructionHandbookDocumentViewProps = {
  complianceId: string
  sections: InstructionHandbookSectionRecord[]
  onSectionsChange: (sections: InstructionHandbookSectionRecord[]) => void
  onAutosave: (sections: InstructionHandbookSectionRecord[]) => Promise<void>
  onCreateVersion: (
    sections: InstructionHandbookSectionRecord[]
  ) => Promise<void>
  isAutosaving: boolean
  isCreatingVersion: boolean
  lastSavedAt: Date | null
  isGenerating: boolean
  onExportPdf: () => void | Promise<void>
  isExportPending: boolean
  generateMenu: React.ReactNode
  onRegenerateSection: (sectionId: string) => void
  contentSyncKey: number
  streamingSectionId: string | null
  streamingContent: string
  onFocusedEditorChange?: (editor: Editor | null, sectionId: string | null) => void
  bubbleMenuContent?: React.ReactNode
  bubbleMenuHidden?: boolean
  /** When true, editor blur does not schedule autosave (rewrite drawer is open). */
  rewritePanelOpen?: boolean
}

export const InstructionHandbookDocumentView = forwardRef<
  ComplianceDocumentViewHandle,
  InstructionHandbookDocumentViewProps
>(function InstructionHandbookDocumentView(props, ref) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const { resolveSectionDisplayTitle } =
    useComplianceDocumentManifestContext()

  const streamingStatusLabel = useMemo(() => {
    if (props.streamingSectionId == null) {
      return t('manualGenerationPage.generating')
    }
    const streamingSection = props.sections.find(
      (section) => section.sectionId === props.streamingSectionId
    )
    const sectionLabel =
      streamingSection != null
        ? getSectionHeadingLabel(
            streamingSection,
            resolveSectionDisplayTitle(streamingSection)
          )
        : props.streamingSectionId
    return t('manualGenerationPage.generatingSection', {
      section: sectionLabel,
    })
  }, [
    props.streamingSectionId,
    props.sections,
    resolveSectionDisplayTitle,
    t,
  ])

  return (
    <ComplianceDocumentView
      ref={ref}
      studioScopeId={props.complianceId}
      sections={props.sections}
      onSectionsChange={props.onSectionsChange}
      onAutosave={props.onAutosave}
      onCreateVersion={props.onCreateVersion}
      isAutosaving={props.isAutosaving}
      isCreatingVersion={props.isCreatingVersion}
      lastSavedAt={props.lastSavedAt}
      isGenerating={props.isGenerating}
      onExportPdf={props.onExportPdf}
      isExportPending={props.isExportPending}
      generateMenu={props.generateMenu}
      onRegenerateSection={props.onRegenerateSection}
      contentSyncKey={props.contentSyncKey}
      sectionDomIdPrefix='instruction-handbook'
      translationKeyPrefix='manualGenerationPage'
      i18nNamespace='machineryDirectiveCompliance'
      inputIdPrefix='instruction-handbook'
      imageUploadScopeId={props.complianceId}
      shouldShowSectionEditor={shouldShowInstructionHandbookSectionEditor}
      streamingSectionId={props.streamingSectionId}
      onFocusedEditorChange={props.onFocusedEditorChange}
      bubbleMenuContent={props.bubbleMenuContent}
      bubbleMenuHidden={props.bubbleMenuHidden}
      rewritePanelOpen={props.rewritePanelOpen}
      streamingPanel={
        <ComplianceDocumentStreamingPanel
          isOpen={props.isGenerating}
          statusLabel={streamingStatusLabel}
          streamingContent={props.streamingContent}
        />
      }
    />
  )
})
